import {AfterViewInit, Component, computed, inject, OnInit, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Actions} from '@ngneat/effects-ng';
import {TranslateService} from '@ngx-translate/core';
import {GridDataResult} from '@progress/kendo-angular-grid';
import {CompositeFilterDescriptor} from '@progress/kendo-data-query';
import {
  IntercomEventsByArea,
  IntercomEventService,
  OrgInfo,
  PermissionsService,
  REF_MODEL_STORE,
  Refmodel,
  RefModelNode,
  RefModelStoreService,
  SnackBarService,
  TakeUntilComponentDestroy,
  User
} from '@q9elements/ui-kit/common';
import {ContextMenuItem, loadEntities} from '@q9elements/ui-kit/components';
import {head, isArray, last} from 'lodash';
import {combineLatest, Observable} from 'rxjs';
import {filter, map, take, tap} from 'rxjs/operators';

import {RmeNodeDaoAbstractService} from '../core/abstract/rme-node-dao-abstract.service';
import {AuthService} from '../core/services/auth.service';
import {IGridColumn} from '../custom-grid/models/grid-column.interface';
import {GridDataSourceService} from '../custom-grid/services/grid-data-source.service';
import {GridSelectorService} from '../custom-grid/services/grid-selector.service';
import {ImpactAssessmentStore} from '../impact-assessment/services/impact-assessment-store';
import {ContextMenuViewService} from '../new-context-menu/services/context-menu-view.service';
import {RefModelService} from '../services/ref-model.service';
import {ImpactGridViewDaoService} from './services/impact-grid-view-dao.service';
import {ImpactGridQuery} from './state/impact-grid.query';
import {CUSTOMIZABLE_COLUMNS, impactGridViewColumns} from './util/constants';

@Component({
  selector: 'app-impact-grid-view',
  templateUrl: './impact-grid-view.component.html',
  styleUrls: ['./impact-grid-view.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ImpactGridViewComponent
  extends TakeUntilComponentDestroy
  implements OnInit, AfterViewInit
{
  private refModel: Refmodel;
  private gridData: GridDataResult[];
  private user: User;
  protected actions: Actions = inject(Actions);

  private filter: CompositeFilterDescriptor;
  readonly idField = '_id';

  public node: RefModelNode;
  public menuItems: ContextMenuItem[];
  public filters = this.impactGridQuery.selectFilters();
  public queryParams: any;
  public userRefModelIds: string[];
  public gridColumns: IGridColumn<unknown>[] = [];
  private columnVisibility: {[field: string]: boolean} = {};
  public ensureAttributeParity: boolean = false;
  public refModelData$: Observable<Refmodel> = this.rmeStore.select(REF_MODEL_STORE.REF_MODEL);
  public orgInfo$: Observable<OrgInfo> = this.rmeStore.select<OrgInfo>(REF_MODEL_STORE.ORG_INFO);
  public selectedNode$: Observable<RefModelNode> = this.rmeStore.select<RefModelNode>(
    REF_MODEL_STORE.SELECTED_NODE
  );
  public showSidebar$: Observable<boolean> = combineLatest([
    this.selectedNode$,
    this.impactGridQuery.selectActiveId()
  ]).pipe(
    map(([selectedNode, selectedKeys]) => {
      return selectedNode && selectedKeys?.length === 1;
    })
  );
  public isFree = this.permissionsService.isFree;
  public referenceCount = this.impactGridQuery.selectEntitiesCount();
  public paywallTitle = computed(() =>
    this.translateService.instant('DEPENDENCY_TREE.PAYWALL_GRID_TITLE', {
      count: this.referenceCount() ?? 0
    })
  );

  constructor(
    private impactAssessmentStore: ImpactAssessmentStore,
    private rmeStore: RefModelStoreService,
    private router: Router,
    private route: ActivatedRoute,
    private impactGridViewDaoService: ImpactGridViewDaoService,
    public gridDataSourceService: GridDataSourceService,
    private gridSelectorService: GridSelectorService,
    private translateService: TranslateService,
    private snackBarService: SnackBarService,
    private nodeService: RmeNodeDaoAbstractService,
    private contextMenuViewService: ContextMenuViewService,
    private refModelService: RefModelService,
    private authService: AuthService,
    private impactGridQuery: ImpactGridQuery,
    private intercomEventService: IntercomEventService,
    private permissionsService: PermissionsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeColumns();
    this.subscribeToRefModelData();
    this.getUserRefModels();
    this.initOptimizeStatuses();
    this.subscribeToQueryParams();
    this.user = this.rmeStore.user;
    this.onRowSelected();
  }

  onRowSelected(): void {
    this.impactGridQuery
      .selectActive()
      .pipe(
        this.takeUntilComponentDestroy(),
        tap(nodes => this.gridSelectorService.handleKeySelectionChange(nodes)),
        map(last)
      )
      .subscribe(node => {
        if (node) {
          this.openSidebarAndSetNode(node);
        }
      });
  }

  ngAfterViewInit() {
    this.initFilterSubscription();
  }

  initializeImpactGridQuery() {
    this.impactGridQuery.store.update({
      refModelId: this.refModel._id,
      selectedNodes: JSON.parse(this.queryParams?.nodes || '[]')
    });
  }

  setLoading(isLoading: boolean) {
    if (!isLoading) {
      return this.gridDataSourceService.stopLoaders();
    }

    this.gridData?.length || this.filter?.filters?.length
      ? (this.gridDataSourceService.upFetching = true)
      : (this.gridDataSourceService.loading = true);
  }

  backToTreeView() {
    return this.router.navigate(['sf-impact-assessment', this.node.refModel, this.node._id]);
  }

  downloadGridViewCsv() {
    if (this.permissionsService.isFree()) {
      return;
    }

    this.impactGridViewDaoService.downloadGridViewCsv(this.refModel, this.queryParams).subscribe(
      () => {
        this.snackBarService.success('REPORTS.SUCCESSFULL_START_CSV');
      },
      ({error}) => {
        const {error: errorMessage} = error;
        const message = errorMessage
          ? errorMessage
          : this.translateService.instant('REPORTS.START_DOWNLOADING_FAILURE');

        this.snackBarService.error(message);
      }
    );
  }

  private initializeColumns(): void {
    const allCustomizableFields = [
      ...CUSTOMIZABLE_COLUMNS.general,
      ...CUSTOMIZABLE_COLUMNS.source,
      ...CUSTOMIZABLE_COLUMNS.dependent
    ];

    allCustomizableFields.forEach(field => {
      this.columnVisibility[field] = false;
    });

    this.refreshColumns();
  }

  public getSourceColumns(): IGridColumn<unknown>[] {
    return impactGridViewColumns.filter(col => CUSTOMIZABLE_COLUMNS.source.includes(col.field));
  }

  public getDependentColumns(): IGridColumn<unknown>[] {
    return impactGridViewColumns.filter(col => CUSTOMIZABLE_COLUMNS.dependent.includes(col.field));
  }

  public getGeneralColumns(): IGridColumn<unknown>[] {
    return impactGridViewColumns.filter(col => CUSTOMIZABLE_COLUMNS.general.includes(col.field));
  }

  public isColumnVisible(field: string): boolean {
    return this.columnVisibility[field] === true;
  }

  public toggleColumn(field: string, visible: boolean): void {
    this.columnVisibility[field] = visible;

    if (this.ensureAttributeParity) {
      this.syncParityColumns(field, visible);
    }

    this.refreshColumns();
  }

  private syncParityColumns(field: string, visible: boolean): void {
    const columnPairs = {
      // Source to Dependent mappings
      sourceParentObject: 'dependentParentObject',
      sourceManagedPackage: 'dependentManagedPackage',
      sourceLastRun: 'dependentLastRun',
      sourceActive: 'dependentActive',
      sourceManaged: 'dependentManaged',
      // Dependent to Source mappings
      dependentParentObject: 'sourceParentObject',
      dependentManagedPackage: 'sourceManagedPackage',
      dependentLastRun: 'sourceLastRun',
      dependentActive: 'sourceActive',
      dependentManaged: 'sourceManaged'
    };

    const pairedField = columnPairs[field];
    if (pairedField) {
      this.columnVisibility[pairedField] = visible;
    }
  }

  public toggleAttributeParity(enabled: boolean): void {
    this.ensureAttributeParity = enabled;
  }

  private refreshColumns(): void {
    const allCustomizableFields = [
      ...CUSTOMIZABLE_COLUMNS.general,
      ...CUSTOMIZABLE_COLUMNS.source,
      ...CUSTOMIZABLE_COLUMNS.dependent
    ];

    this.gridColumns = impactGridViewColumns.map(column => {
      if (allCustomizableFields.includes(column.field)) {
        return {
          ...column,
          hidden: !this.columnVisibility[column.field]
        };
      }
      return column;
    });
  }

  public resetFilters() {
    this.setLoading(true);
    this.gridSelectorService.resetSelectedKeys();
    this.gridDataSourceService.filterChange({logic: 'and', filters: []});
  }

  subscribeToQueryParams() {
    this.route.queryParams.pipe(take(1)).subscribe(queryParams => {
      this.queryParams = queryParams;
      this.subscribeToNodeData();

      const nodes = JSON.parse(this.queryParams?.nodes);

      if (nodes.length === 1) {
        const nodeId: string = head(nodes);

        this.getNodeData(nodeId);
      }

      this.initializeImpactGridQuery();
      this.actions.dispatch(loadEntities());
    });
  }

  private subscribeToNodeData() {
    this.impactAssessmentStore
      .select('nodeData')
      .pipe(
        take(1),
        filter(nodeData => this.queryParams?.dependencyTree && !nodeData)
      )
      .subscribe((node: any) => (this.node = node));
  }

  private subscribeToRefModelData() {
    this.rmeStore
      .select(REF_MODEL_STORE.REF_MODEL)
      .pipe(take(1))
      .subscribe((refModel: any) => {
        this.intercomEventService.sendIntercomEvent(
          refModel,
          IntercomEventsByArea.SALESFORCE_METADATA.DEPENDENCIES_GRID_OPENED
        );
        this.refModel = refModel;
      });
  }

  private getNodeData(nodeId: string) {
    this.nodeService
      .getNode(this.refModel._id, nodeId)
      .pipe(take(1))
      .subscribe(node => {
        this.node = node;
      });
  }

  private getUserRefModels() {
    this.rmeStore
      .select(REF_MODEL_STORE.USER_REF_MODELS_LIST)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((refModelsList: string[]) => (this.userRefModelIds = refModelsList));
  }

  private initOptimizeStatuses() {
    this.refModelService.getOptimizeStatuses().subscribe();
  }

  private openSidebarAndSetNode(selectedNode) {
    const {dependentRefModelId: refModelId, dependentId: id} = selectedNode;
    this.menuItems = [];

    const observablesArray = [
      this.refModelService.getRefModelMeta(refModelId),
      this.nodeService.getNode(refModelId, id)
    ];

    combineLatest(observablesArray)
      .pipe(take(1))
      .subscribe(([refModel, node]) => {
        this.rmeStore.set(REF_MODEL_STORE.REF_MODEL, refModel);
        this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE, node);

        this.getContextMenuItems();
      });
  }

  private initFilterSubscription() {
    this.gridDataSourceService.filters$.subscribe(filterData => {
      this.filter = filterData;
    });
  }

  private getContextMenuItems(): void {
    this.menuItems = this.contextMenuViewService.getDependencyGridMenuItems() as ContextMenuItem[];

    this.menuItems.forEach(item => {
      if (isArray(item.items)) {
        item.items = item.items.filter(subItem => subItem !== null && subItem !== undefined);
      }
    });
  }
}
